import Icons from "@/assets/icons";
import { ActionButton } from "@/components/ActionButton";
import { FontFamilies } from "@/constants/Typography";
import FBTrackingService from "@/services/fbTrackingService";
import useFinanceDataStore from "@/store/financeData";
import { router } from "expo-router";
import React, { useEffect, useState } from "react";
import {
  Text,
  TextInput,
  TouchableOpacity,
  View,
  ActivityIndicator,
  ScrollView,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { StyleSheet } from "@/utils/appStyleSheet";
import { Ionicons } from "@expo/vector-icons";
import { Colors } from "@/constants/Colors";
import { SCREEN_WIDTH } from "@/constants/Sizes";
import useWindow from "@/hooks/useWindow";

// Helper to format cents to display string
const formatCentsToDisplay = (cents: number): string => {
  const dollars = cents / 100;
  return (
    "$" +
    dollars.toLocaleString("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })
  );
};

// Helper to parse display string to cents
const parseDisplayToCents = (display: string): number => {
  const numStr = display.replace(/[$,]/g, "");
  const dollars = parseFloat(numStr) || 0;
  return Math.round(dollars * 100);
};

const SetupIncomeScreen = () => {
  const { isDesktop } = useWindow();
  const { financeData, updateIncome } = useFinanceDataStore();
  const [isEditing, setIsEditing] = useState(false);
  const [incomeValue, setIncomeValue] = useState("$0");
  const [isSaving, setIsSaving] = useState(false);

  // Initialize income from store
  useEffect(() => {
    if (financeData?.analysis?.income?.totalMonthly) {
      setIncomeValue(
        formatCentsToDisplay(financeData.analysis.income.totalMonthly),
      );
    }
  }, [financeData]);

  useEffect(() => {
    FBTrackingService.trackViewContent("my-money-setup-income");
  }, []);

  const handleContinue = async () => {
    if (isEditing) {
      // Save the edited income via API
      setIsSaving(true);
      const incomeCents = parseDisplayToCents(incomeValue);
      const success = await updateIncome(incomeCents);
      setIsSaving(false);

      if (success) {
        setIsEditing(false);
      }
      return;
    }
    // Navigate to set budget screen
    router.push("/my-money/set-budget");
  };

  const handleEditIncome = () => {
    setIsEditing(true);
  };

  const handleBack = () => {
    setIsEditing(false);
  };

  const formatWithCommas = (numStr: string) => {
    const parts = numStr.split(".");
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    return parts.join(".");
  };

  const handleIncomeChange = (text: string) => {
    if (!text.startsWith("$")) {
      text = "$" + text.replace(/\$/g, "");
    }

    const afterDollar = text.slice(1).replace(/,/g, "");

    let cleaned = "";
    let hasDot = false;

    for (const char of afterDollar) {
      if (char >= "0" && char <= "9") {
        cleaned += char;
      } else if (char === "." && !hasDot) {
        cleaned += char;
        hasDot = true;
      }
    }

    if (cleaned === "" || cleaned === ".") {
      setIncomeValue("$0");
      return;
    }

    if (cleaned.length > 1 && cleaned[0] === "0" && cleaned[1] !== ".") {
      cleaned = cleaned.replace(/^0+/, "") || "0";
    }

    setIncomeValue("$" + formatWithCommas(cleaned));
  };

  // Edit Income Mode
  if (isEditing) {
    return (
      <SafeAreaView style={[styles.container, isDesktop && styles.containerDesktop]}>
        <ScrollView
          style={styles.editScrollView}
          contentContainerStyle={styles.editScrollContent}
          keyboardShouldPersistTaps="handled"
        >
          <View style={styles.editContent}>
            {/* Back Button */}
            <TouchableOpacity style={styles.backButton} onPress={handleBack}>
              <Ionicons name="chevron-back" size={28} color="#161D14" />
            </TouchableOpacity>

            {/* Title and Description */}
            <View style={styles.editHeader}>
              <Text style={styles.editTitle}>Enter your income</Text>
              <Text style={styles.editSubtitle}>
                Please enter your monthly income so Sparrow can calculate your
                budget and savings.
              </Text>
            </View>

            {/* Input Field */}
            <View style={styles.inputSection}>
              <Text style={styles.inputLabel}>Your monthly income</Text>
              <View style={styles.inputContainer}>
                <TextInput
                  style={styles.incomeInput}
                  value={incomeValue}
                  onChangeText={handleIncomeChange}
                  keyboardType="decimal-pad"
                  selection={{
                    start: incomeValue.length,
                    end: incomeValue.length,
                  }}
                />
              </View>
            </View>
          </View>
        </ScrollView>

        {/* Continue Button */}
        <View style={styles.buttonContainer}>
          <ActionButton
            title={isSaving ? "" : "Continue"}
            type="primary"
            onPress={handleContinue}
            style={styles.continueButton}
            disabled={isSaving}
          >
            {isSaving && <ActivityIndicator color="#FFFFFF" />}
          </ActionButton>
        </View>
      </SafeAreaView>
    );
  }

  // Display Mode (default)
  return (
    <SafeAreaView style={[styles.container, isDesktop && styles.containerDesktop]}>
      {/* Header with Logo */}
      <View style={styles.header}>
        <Icons.LogoWithTextBlack width={142} height={34} />
      </View>

      {/* Content */}
      <View style={styles.content}>
        {/* Description */}
        <Text style={styles.description}>
          Based on your account, Sparrow identified a recurring pattern. Your
          income is:
        </Text>

        {/* Income Display */}
        <View style={styles.incomeContainer}>
          <Text style={styles.incomeAmount}>
            {incomeValue} per month
          </Text>
        </View>
      </View>

      {/* Bottom Buttons */}
      <View style={styles.buttonContainer}>
        <ActionButton
          title="Continue"
          type="primary"
          onPress={handleContinue}
          style={styles.continueButton}
        />
        <TouchableOpacity style={styles.editButton} onPress={handleEditIncome}>
          <Text style={styles.editButtonText}>Edit income</Text>
        </TouchableOpacity>
      </View>
    </SafeAreaView>
  );
};

export default SetupIncomeScreen;

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#FFFFFF",
  },
  containerDesktop: {
    maxWidth: 600,
    alignSelf: "center",
    width: "100%",
  },
  header: {
    alignItems: "center",
    paddingTop: 16,
    paddingBottom: 24,
  },
  content: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    paddingHorizontal: 32,
    gap: 32,
  },
  description: {
    fontSize: 18,
    fontFamily: FontFamilies.Inter.Regular,
    color: "#161D14",
    opacity: 0.7,
    textAlign: "center",
    lineHeight: 28,
    letterSpacing: -0.36,
  },
  incomeContainer: {
    alignItems: "center",
    gap: 8,
  },
  incomeAmount: {
    fontSize: 48,
    fontFamily: FontFamilies.InterTight.SemiBold,
    color: "#161D14",
    textAlign: "center",
    lineHeight: 44,
  },
  buttonContainer: {
    paddingHorizontal: 20,
    paddingBottom: 16,
    gap: 8,
    alignItems: "center",
  },
  continueButton: {
    width: "100%",
    height: 56,
    borderRadius: 100,
    maxWidth: SCREEN_WIDTH,
  },
  editButton: {
    paddingVertical: 8,
    paddingHorizontal: 24,
    opacity: 0.7,
  },
  editButtonText: {
    fontSize: 15,
    fontFamily: FontFamilies.Inter.Medium,
    color: "#0F9129",
    textAlign: "center",
    lineHeight: 23,
  },
  // Edit mode styles
  editScrollView: {
    flex: 1,
  },
  editScrollContent: {
    flexGrow: 1,
  },
  editContent: {
    flex: 1,
    paddingHorizontal: 16,
    gap: 28,
  },
  backButton: {
    width: 28,
    height: 28,
    justifyContent: "center",
    alignItems: "center",
    marginTop: 8,
  },
  editHeader: {
    gap: 8,
  },
  editTitle: {
    fontSize: 30,
    fontFamily: FontFamilies.Inter.Medium,
    color: "#161D14",
    lineHeight: 37,
  },
  editSubtitle: {
    fontSize: 15,
    fontFamily: FontFamilies.Inter.Regular,
    color: "#161D14",
    opacity: 0.7,
    lineHeight: 23,
  },
  inputSection: {
    gap: 8,
  },
  inputLabel: {
    fontSize: 14,
    fontFamily: FontFamilies.Inter.Regular,
    color: "#161D14",
  },
  inputContainer: {
    borderWidth: 1,
    borderColor: "#E5E7EB",
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  incomeInput: {
    fontSize: 16,
    fontFamily: FontFamilies.Inter.Regular,
    color: "#161D14",
    padding: 0,
  },
});
